import React from 'react';
import PropTypes from 'prop-types';

import { StudyItem } from '../StudyItem';
import { StudyBrowserSort } from '../StudyBrowserSort';
import { StudyBrowserViewOptions } from '../StudyBrowserViewOptions';
import { ScrollArea } from '../ScrollArea';

const noop = () => {};

/**
 * Mobile-optimized StudyBrowser with horizontal thumbnail strip layout
 * Designed for bottom panel placement on mobile devices
 */
const StudyBrowserMobile = ({
  tabs,
  activeTabName,
  expandedStudyInstanceUIDs,
  onClickTab = noop,
  onClickStudy = noop,
  onClickThumbnail = noop,
  onDoubleClickThumbnail = noop,
  onClickUntrack = noop,
  activeDisplaySetInstanceUIDs,
  servicesManager,
  showSettings,
  viewPresets,
  ThumbnailMenuItems,
  StudyMenuItems,
}: withAppTypes) => {
  const getTabContent = () => {
    let tabData = tabs.find(tab => tab.name === activeTabName);
    
    // Fallback to first tab if no match found
    if (!tabData && tabs.length > 0) {
      tabData = tabs[0];
    }
    
    
    const viewPreset = viewPresets
      ? viewPresets.filter(preset => preset.selected)[0]?.id
      : 'thumbnails';
      
    // For mobile, we want to show all thumbnails in a horizontal strip
    // Instead of grouping by study, flatten all displaySets
    const allDisplaySets = tabData?.studies?.reduce((acc, study) => {
      
      const studyDisplaySets = study.displaySets?.map(displaySet => ({
        ...displaySet,
        studyInstanceUid: study.studyInstanceUid,
        studyDescription: study.description,
        studyDate: study.date,
      })) || [];
      return acc.concat(studyDisplaySets);
    }, []);
    

    if (!allDisplaySets || allDisplaySets.length === 0) {
      return (
        <div className="flex items-center justify-center h-full px-2">
          <div className="text-white text-sm">No series available</div>
        </div>
      );
    }

    return (
      <div className="flex flex-row gap-1 px-1 py-0.5" style={{ width: `${allDisplaySets.length * 88}px` }}>
        {allDisplaySets.map((displaySet, index) => (
          <div
            key={`${displaySet.studyInstanceUid}-${displaySet.displaySetInstanceUID}-${index}`}
            className="flex-shrink-0"
          >
            {/* Mobile thumbnail - touch-optimized */}
            <div
              className="relative w-20 h-20 bg-black rounded cursor-pointer hover:opacity-80 transition-opacity"
              onClick={() => onClickThumbnail(displaySet)}
              onDoubleClick={() => onDoubleClickThumbnail(displaySet)}
            >
              {displaySet.imageSrc ? (
                <img
                  src={displaySet.imageSrc}
                  alt={displaySet.imageAltText || `Series ${displaySet.seriesNumber}`}
                  className="w-full h-full object-cover rounded"
                  draggable={false}
                  onLoad={() => {}}
                  onError={() => {}}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-xs bg-gray-700 rounded">
                  {displaySet.seriesNumber || 'N/A'}
                </div>
              )}
              
              {/* Series number overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-60 text-white text-xs px-1 py-0.5 rounded-b">
                S: {displaySet.seriesNumber || 'N/A'}
              </div>
              
              {/* Active indicator */}
              {activeDisplaySetInstanceUIDs?.includes(displaySet.displaySetInstanceUID) && (
                <div className="absolute top-0 right-0 w-3 h-3 bg-blue-500 rounded-full transform translate-x-1 -translate-y-1"></div>
              )}
              
              {/* Tracked indicator */}
              {displaySet.isTracked && (
                <div className="absolute top-0 left-0 w-3 h-3 bg-green-500 rounded-full transform -translate-x-1 -translate-y-1"></div>
              )}
              
              {/* Loading progress indicator */}
              {displaySet.loadingProgress != null && displaySet.loadingProgress < 1 && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded">
                  <div className="text-white text-xs font-semibold">
                    {`${Math.round(displaySet.loadingProgress * 100)}%`}
                  </div>
                </div>
              )}
              
            </div>
            
            {/* Series info below thumbnail */}
            <div className="w-20 mt-0.5">
              <div className="text-xs text-white truncate leading-tight" title={displaySet.description}>
                {displaySet.description || 'Unknown'}
              </div>
              <div className="text-xs text-gray-400 leading-tight">
                {displaySet.numInstances} imgs
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="bg-bkg-low h-28 flex flex-col">
      {/* Mobile header - compact */}
      {showSettings && (
        <div className="bg-bkg-low flex h-10 items-center justify-between px-2 border-b border-gray-700">
          <StudyBrowserViewOptions
            tabs={tabs}
            onSelectTab={onClickTab}
            activeTabName={activeTabName}
          />
          <StudyBrowserSort servicesManager={servicesManager} />
        </div>
      )}
      
      {/* Horizontal scrollable thumbnail strip */}
      <div className="flex-1 overflow-hidden">
        <div
          className="h-full w-full overflow-x-auto overflow-y-hidden"
          data-cy={'studyBrowser-panel-mobile'}
          style={{
            scrollbarHeight: '6px',
            minHeight: '0',
            WebkitOverflowScrolling: 'touch', // Enable smooth scrolling on iOS
            touchAction: 'pan-x', // Allow horizontal pan gestures
            scrollbarWidth: 'thin', // Firefox - always show thin scrollbar
          }}
        >
          {getTabContent()}
        </div>
      </div>
    </div>
  );
};

StudyBrowserMobile.propTypes = {
  onClickTab: PropTypes.func.isRequired,
  onClickStudy: PropTypes.func,
  onClickThumbnail: PropTypes.func,
  onDoubleClickThumbnail: PropTypes.func,
  onClickUntrack: PropTypes.func,
  activeTabName: PropTypes.string.isRequired,
  expandedStudyInstanceUIDs: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeDisplaySetInstanceUIDs: PropTypes.arrayOf(PropTypes.string),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      studies: PropTypes.arrayOf(
        PropTypes.shape({
          studyInstanceUid: PropTypes.string.isRequired,
          date: PropTypes.string,
          numInstances: PropTypes.number,
          modalities: PropTypes.string,
          description: PropTypes.string,
          displaySets: PropTypes.arrayOf(
            PropTypes.shape({
              displaySetInstanceUID: PropTypes.string.isRequired,
              imageSrc: PropTypes.string,
              imageAltText: PropTypes.string,
              seriesDate: PropTypes.string,
              seriesNumber: PropTypes.any,
              numInstances: PropTypes.number,
              description: PropTypes.string,
              componentType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage'])
                .isRequired,
              isTracked: PropTypes.bool,
              dragData: PropTypes.shape({
                type: PropTypes.string.isRequired,
              }),
            })
          ),
        })
      ),
    })
  ).isRequired,
  servicesManager: PropTypes.object.isRequired,
  showSettings: PropTypes.bool,
  viewPresets: PropTypes.array,
  ThumbnailMenuItems: PropTypes.array,
  StudyMenuItems: PropTypes.array,
};

export { StudyBrowserMobile };
export default StudyBrowserMobile;